import type { Notification, NotificationType } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";

interface NotificationTypeIconProps {
  notification: Notification;
  size?: number;
}

type IconConfig = {
  iconName: string;
  bg: string;
  fg: string;
};

const iconConfig: Record<NotificationType, IconConfig> = {
  // Hang events → yellow bolt
  new_hang: { iconName: "bolt", bg: "bg-core-yellow", fg: "text-core-deepSlate" },
  hang_update: { iconName: "bolt", bg: "bg-core-yellow", fg: "text-core-deepSlate" },
  hang_canceled: { iconName: "bolt", bg: "bg-core-yellow", fg: "text-core-deepSlate" },
  hang_response: { iconName: "bolt", bg: "bg-core-yellow", fg: "text-core-deepSlate" },

  // Event (incl reminder) → blue calendar
  new_event: { iconName: "calendar", bg: "bg-core-blue", fg: "text-core-offWhite" },
  event_update: { iconName: "calendar", bg: "bg-core-blue", fg: "text-core-offWhite" },
  event_canceled: { iconName: "calendar", bg: "bg-core-blue", fg: "text-core-offWhite" },
  event_reminder: { iconName: "calendar", bg: "bg-core-blue", fg: "text-core-offWhite" },

  // Hang reaction → blue hands (using bolt as fallback)
  hang_reaction: { iconName: "bolt", bg: "bg-core-blue", fg: "text-core-offWhite" },

  // New member → surface with plus
  new_member: { iconName: "plus", bg: "bg-surface-1", fg: "text-text-primary" },

  // Member left → red user
  member_left: { iconName: "user", bg: "bg-core-red", fg: "text-core-offWhite" },

  membership_request: { iconName: "plus", bg: "bg-surface-1", fg: "text-text-primary" },
  membership_approved: { iconName: "plus", bg: "bg-surface-1", fg: "text-text-primary" },
  club_update: { iconName: "comment", bg: "bg-core-olive", fg: "text-core-offWhite" },

  // Posts
  new_post: { iconName: "memo", bg: "bg-core-olive", fg: "text-core-offWhite" },
  post_comment: { iconName: "comment", bg: "bg-core-olive", fg: "text-core-offWhite" },
  post_like: { iconName: "heart", bg: "bg-core-red", fg: "text-core-offWhite" },
  comment_like: { iconName: "heart", bg: "bg-core-red", fg: "text-core-offWhite" },

  // Contact notifications → olive (brand green) user/group icons
  contact_joined: { iconName: "user", bg: "bg-core-olive", fg: "text-core-offWhite" },
  contact_joined_club: { iconName: "user-group", bg: "bg-core-olive", fg: "text-core-offWhite" },
  contact_started_club: { iconName: "user-group", bg: "bg-core-olive", fg: "text-core-offWhite" },
  contact_rsvp_event: { iconName: "calendar", bg: "bg-core-blue", fg: "text-core-offWhite" },
  contact_created_event: { iconName: "calendar-plus", bg: "bg-core-blue", fg: "text-core-offWhite" },

  // Mentions
  mention: { iconName: "at", bg: "bg-core-deepSlate", fg: "text-core-offWhite" },

  // System → red
  system: { iconName: "bell", bg: "bg-core-red", fg: "text-core-offWhite" },
};

/**
 * NotificationTypeIcon - Displays a circular icon representing the notification type
 * with appropriate background color and icon.
 */
export function NotificationTypeIcon({ notification, size = 32 }: NotificationTypeIconProps) {
  const config = iconConfig[notification.notification_type] || iconConfig.system;

  return (
    <div
      className={`flex items-center justify-center rounded-full ${config.bg}`}
      style={{ width: size, height: size }}
    >
      <Icon name={config.iconName} size={size * 0.5} className={config.fg} />
    </div>
  );
}
